import { useState, useEffect } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { Menu, X, ArrowRight } from "lucide-react";
import Logo from "@/components/Logo";
import { Button } from "@/components/ui/button";
import {
  Sheet, SheetContent, SheetTrigger, SheetClose, SheetTitle,
} from "@/components/ui/sheet";
import { useAuth } from "@/context/AuthContext";
import { useLanguage } from "@/context/LanguageContext";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import AiOrb from "@/components/marketing/AiOrb";

const navKeys = [
  { key: "home", to: "/" },
  { key: "about", to: "/about" },
  { key: "solutions", to: "/solutions" },
  { key: "ai", to: "/ai" },
  { key: "careers", to: "/careers" },
  { key: "contact", to: "/contact" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { user } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const navItems = navKeys.map((n) => ({ key: n.key, label: t(`nav.${n.key}`), to: n.to }));

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const isAuthed = user && user !== false;
  const slug = (item) => item.key;

  return (
    <header className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${scrolled ? "vora-glass border-b border-white/5" : "bg-transparent"}`} data-testid="main-navbar">
      <nav className="mx-auto max-w-7xl px-5 sm:px-8 h-16 flex items-center justify-between">
        <Logo />

        <div className="hidden lg:flex items-center gap-1">
          {navItems.map((item) =>
            item.to.startsWith("/#") ? (
              <a key={item.key} href={item.to} className="px-3.5 py-2 text-sm text-slate-300 hover:text-white rounded-lg transition-colors" data-testid={`nav-${slug(item)}`}>{item.label}</a>
            ) : (
              <NavLink key={item.key} to={item.to} className={({ isActive }) => `px-3.5 py-2 text-sm rounded-lg transition-colors ${isActive ? "text-white" : "text-slate-300 hover:text-white"}`} data-testid={`nav-${slug(item)}`}>{item.label}</NavLink>
            )
          )}
        </div>

        <div className="hidden lg:flex items-center gap-3">
          <LanguageSwitcher />
          <AiOrb label={t("nav.askAi")} />
          {isAuthed ? (
            <Button onClick={() => navigate("/app")} className="bg-[#4F8CFF] hover:bg-[#3f7af0] text-white rounded-full px-5" data-testid="nav-dashboard-btn">
              {t("nav.dashboard")} <ArrowRight className="ml-1.5 h-4 w-4" />
            </Button>
          ) : (
            <Button onClick={() => navigate("/early-access")} className="bg-white hover:bg-slate-100 text-[#07111F] font-semibold rounded-full px-5" data-testid="nav-get-early-access">{t("nav.earlyAccess")}</Button>
          )}
        </div>

        <div className="lg:hidden">
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <button className="p-2 text-white rounded-lg hover:bg-white/5" aria-label="Open menu" data-testid="mobile-menu-trigger"><Menu className="h-6 w-6" /></button>
            </SheetTrigger>
            <SheetContent side="right" className="w-[82vw] max-w-sm bg-[#07111F] border-l border-white/10 p-0">
              <SheetTitle className="sr-only">Menu</SheetTitle>
              <div className="flex items-center justify-between px-5 h-16 border-b border-white/10">
                <Logo onClick={() => setOpen(false)} />
                <SheetClose asChild>
                  <button className="p-2 text-slate-300" aria-label="Close menu" data-testid="mobile-menu-close"><X className="h-5 w-5" /></button>
                </SheetClose>
              </div>
              <div className="flex flex-col p-5 gap-1">
                {navItems.map((item) => (
                  <a key={item.key} href={item.to} onClick={() => setOpen(false)} className="px-3 py-3 text-base text-slate-200 hover:text-white rounded-lg hover:bg-white/5" data-testid={`mobile-nav-${slug(item)}`}>{item.label}</a>
                ))}
                <div className="h-px bg-white/10 my-4" />
                <div className="flex items-center justify-between px-3 pb-3">
                  <span className="text-xs text-slate-500">{t("footer.language")}</span>
                  <LanguageSwitcher compact={false} />
                </div>
                <div className="h-px bg-white/10 my-1" />
                {isAuthed ? (
                  <Button onClick={() => { setOpen(false); navigate("/app"); }} className="bg-[#4F8CFF] text-white rounded-full" data-testid="mobile-dashboard-btn">{t("nav.dashboard")}</Button>
                ) : (
                  <Button onClick={() => { setOpen(false); navigate("/early-access"); }} className="bg-white text-[#07111F] font-semibold rounded-full" data-testid="mobile-get-early-access">{t("nav.earlyAccess")}</Button>
                )}
                <div className="mt-3 flex justify-center">
                  <AiOrb label={t("nav.askAi")} />
                </div>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </nav>
    </header>
  );
}
